import React, { useState, useEffect, useRef } from 'react';
import { Mic, Upload, Clock, FileText, AlertCircle, StopCircle, Play, Pause } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import secureStorage from '../utils/secureStorage';
import { API } from '../App';
import { toast, Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';

const VoiceBiomarker = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audioSummary, setAudioSummary] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [biomarkerHistory, setBiomarkerHistory] = useState([]);
  const [selectedBiomarker, setSelectedBiomarker] = useState(null);
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    fetchBiomarkerHistory();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const fetchBiomarkerHistory = async () => {
    try {
      const token = secureStorage.getToken();
      const response = await axios.get(`${API}/voice-biomarker/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBiomarkerHistory(response.data.biomarkers);
      }
    } catch (error) {
      console.error('Failed to fetch biomarker history:', error);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Failed to access microphone. Please grant permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      toast.success('Recording stopped');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setAudioBlob(null);
      setAudioUrl(null);
      setAudioSummary(null);
      setAnalysis(null);
      setSelectedBiomarker(null);
      toast.success('File selected');
    }
  };

  const handleUpload = async () => {
    let fileToUpload = selectedFile;
    
    if (!fileToUpload && audioBlob) {
      // Convert recorded audio to file
      fileToUpload = new File([audioBlob], `recording_${Date.now()}.webm`, { type: 'audio/webm' });
    }

    if (!fileToUpload) {
      toast.error('Please record or select an audio file first');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const token = secureStorage.getToken();
      const response = await axios.post(`${API}/voice-biomarker/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setAudioSummary(response.data.audio_summary);
        setAnalysis(response.data.analysis);
        toast.success('Voice sample analyzed successfully!');
        fetchBiomarkerHistory();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze voice sample');
    } finally {
      setLoading(false);
    }
  };

  const viewBiomarkerDetails = async (filename) => {
    try {
      const token = secureStorage.getToken();
      const response = await axios.get(`${API}/voice-biomarker/biomarker/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSelectedBiomarker(response.data.biomarker);
        setAudioSummary(response.data.biomarker.audio_summary);
        setAnalysis(response.data.biomarker.analysis);
        setSelectedFile(null);
        setAudioBlob(null);
        setAudioUrl(null);
      }
    } catch (error) {
      console.error('Failed to fetch biomarker details:', error);
      toast.error('Failed to load biomarker details');
    }
  };

  const handleNewAnalysis = () => {
    setSelectedFile(null);
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioSummary(null);
    setAnalysis(null);
    setSelectedBiomarker(null);
    setRecordingTime(0);
  };

  const togglePlayPause = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        audioPlayerRef.current.pause();
      } else {
        audioPlayerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <Navigation user={user} setUser={setUser} />
      <Toaster position="top-center" richColors />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-xl">
              <Mic className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {t('voiceBiomarker.title')}
              </h1>
              <p className="text-gray-600 text-base mt-1">
                {t('voiceBiomarker.subtitle')}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Recording & Upload */}
          <div className="space-y-6">
            {/* Recording Card */}
            <Card className="p-6 shadow-lg">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Mic size={24} className="text-purple-500" />
                {t('voiceBiomarker.recordVoiceSample')}
              </h2>

              <div className="space-y-4">
                {!isRecording && !audioUrl && !selectedFile && (
                  <div className="text-center py-8">
                    <Button
                      onClick={startRecording}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-6 text-lg"
                    >
                      <Mic className="mr-2" size={24} />
                      {t('voiceBiomarker.startRecording')}
                    </Button>
                  </div>
                )}

                {isRecording && (
                  <div className="text-center py-8">
                    <div className="mb-6">
                      <div className="inline-block animate-pulse bg-red-500 rounded-full p-6 mb-4">
                        <Mic className="text-white" size={48} />
                      </div>
                      <div className="text-3xl font-mono font-bold text-gray-800">
                        {formatTime(recordingTime)}
                      </div>
                      <p className="text-gray-600 mt-2">Recording in progress...</p>
                    </div>
                    <Button
                      onClick={stopRecording}
                      className="w-full bg-red-500 hover:bg-red-600 text-white py-6 text-lg"
                    >
                      <StopCircle className="mr-2" size={24} />
                      {t('voiceBiomarker.stopRecording')}
                    </Button>
                  </div>
                )}

                {audioUrl && (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-medium text-gray-700">{t('voiceBiomarker.recordingPreview')}</span>
                        <span className="text-sm text-gray-600">{formatTime(recordingTime)}</span>
                      </div>
                      <audio
                        ref={audioPlayerRef}
                        src={audioUrl}
                        onEnded={() => setIsPlaying(false)}
                        className="w-full"
                        controls
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleUpload}
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                      >
                        {loading ? 'Analyzing...' : 'Analyze Voice'}
                      </Button>
                      <Button
                        onClick={handleNewAnalysis}
                        variant="outline"
                        className="flex-1"
                      >
                        New Recording
                      </Button>
                    </div>
                  </div>
                )}

                {!isRecording && !audioUrl && (
                  <div>
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">{t('voiceBiomarker.or')}</span>
                      </div>
                    </div>

                    <label className="block">
                      <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all">
                        <Upload size={40} className="mx-auto mb-3 text-purple-500" />
                        <p className="text-gray-700 font-medium mb-2">{t('voiceBiomarker.uploadAudioFile')}</p>
                        <p className="text-sm text-gray-500">Supports: MP3, WAV, WEBM, M4A (max 25MB)</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="audio/mp3,audio/wav,audio/webm,audio/mpeg,audio/m4a,audio/mpga"
                        onChange={handleFileSelect}
                      />
                    </label>

                    {selectedFile && (
                      <div className="mt-4 space-y-3">
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg">
                          <p className="font-medium text-gray-700 mb-1">{selectedFile.name}</p>
                          <p className="text-sm text-gray-600">
                            Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <Button
                            onClick={handleUpload}
                            disabled={loading}
                            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                          >
                            {loading ? 'Analyzing...' : 'Analyze Voice'}
                          </Button>
                          <Button
                            onClick={handleNewAnalysis}
                            variant="outline"
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Audio Summary */}
            {audioSummary && (
              <Card className="p-6 shadow-lg">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FileText size={24} className="text-blue-500" />
                  Recording Summary
                </h2>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-gray-800 leading-relaxed">{audioSummary}</p>
                </div>
              </Card>
            )}

            {analysis && (
              <Card className="p-6 shadow-lg">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FileText size={24} className="text-purple-500" />
                  Health Analysis
                </h2>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg">
                  <div className="prose max-w-none">
                    <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                      {analysis}
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                    <div className="flex items-start gap-2">
                      <AlertCircle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-yellow-800">
                        <strong>Disclaimer:</strong> This is an AI-powered analysis and should not replace professional medical diagnosis. Consult a healthcare provider for proper evaluation.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Analysis History */}
          <div>
            <Card className="p-6 shadow-lg">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Clock size={24} className="text-green-500" />
                {t('voiceBiomarker.analysisHistory')}
              </h2>

              {biomarkerHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Mic size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">{t('voiceBiomarker.noPreviousAnalyses')}</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
                  {biomarkerHistory.map((biomarker, index) => (
                    <div
                      key={`item-${index}`}
                      onClick={() => viewBiomarkerDetails(biomarker.filename)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedBiomarker?.filename === biomarker.filename
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-800">{biomarker.filename}</h3>
                        <Mic size={20} className="text-purple-500" />
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <Clock size={14} />
                        <span>{new Date(biomarker.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Size: {biomarker.file_size_mb} MB
                      </div>
                      {biomarker.audio_summary && (
                        <div className="mt-2 text-xs text-gray-600 line-clamp-2">
                          {biomarker.audio_summary}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceBiomarker;
